// Catálogo público de una compañía (/catalogo/:slug) — sin login, pensado
// para que cualquier cliente lo abra desde un link compartido por
// WhatsApp/redes: arma un carrito, deja sus datos, y al enviar el pedido se
// guarda en el sistema y se abre WhatsApp con el mensaje ya armado.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { getCatalogoPublico, crearPedidoPublico } from '../services/apiService';
import { formatCOP } from '../utils/formatters';
import { matchesSearch } from '../utils/normalize';
import QuantityInput from '../components/QuantityInput';
import ProductDetailModal from '../components/ProductDetailModal';
import DigitalPurchaseModal from '../components/DigitalPurchaseModal';
import AutorizacionDatos, { AUTORIZACION_FALTANTE_MSG } from '../components/AutorizacionDatos';
import PoliticaDatosTienda from '../components/PoliticaDatosTienda';
import { FaStore, FaBoxOpen, FaExternalLinkAlt, FaShoppingCart, FaTimes, FaWhatsapp, FaSearch, FaTimesCircle, FaExpand, FaExclamationTriangle } from 'react-icons/fa';

// Bucket para productos sin categoría: nunca deben quedar fuera del
// filtro, así que en vez de excluirlos caen todos acá.
const OTROS_LABEL = 'Otros';

// En staging hay un banner fijo arriba (~28px, ver StagingBanner) que taparía
// la barra pegada; en producción no existe, así que ahí se pega al borde.
const STICKY_TOP = process.env.REACT_APP_ENVIRONMENT === 'staging' ? 'top-7' : 'top-0';

const PublicCatalogPage = () => {
  const { slug } = useParams();
  // Catálogo por vendedor (v1.4): ?v=codigo en el link que compartió el
  // vendedor. Se lee una sola vez al entrar y viaja con cada pedido — el
  // propio backend decide si ese código sirve o no (código inexistente,
  // vendedor inactivo, etc.), acá no se valida nada, solo se reenvía.
  const [searchParams] = useSearchParams();
  const codigoVendedor = searchParams.get('v');
  const [catalogo, setCatalogo] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tres estados de error bien distintos, no uno solo: un 404 real ("este
  // catálogo no existe") es un hecho permanente, pero un fallo de red o un
  // 5xx del servidor son transitorios — mostrarle a un cliente sin señal el
  // mismo "catálogo no encontrado" que a uno con un link roto de verdad es
  // engañoso y no le da nada que hacer. Ver getCatalogoPublico en
  // apiService.js: err.status viene del backend (404/5xx); un fetch que
  // falla de plano (offline, DNS, timeout) no tiene .status en absoluto.
  const [notFound, setNotFound] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState(null); // null = "Todas"

  const [cart, setCart] = useState([]); // [{ productId, nombre, precioVenta, cantidad, imagen }]
  const [detalleId, setDetalleId] = useState(null); // producto abierto en el modal de detalle
  const [compraDigital, setCompraDigital] = useState(null); // producto digital que se está comprando
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [tipoEntrega, setTipoEntrega] = useState('RECOGE');
  const [direccionEntrega, setDireccionEntrega] = useState('');
  const [autorizaDatos, setAutorizaDatos] = useState(false);
  const [aceptaPromociones, setAceptaPromociones] = useState(false);
  const [verPolitica, setVerPolitica] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const fetchCatalogo = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);
      setNetworkError(false);
      setServerError(false);
      const data = await getCatalogoPublico(slug);
      setCatalogo(data);
    } catch (err) {
      if (err.status === 404) {
        setNotFound(true);
      } else if (typeof err.status === 'number') {
        // Cualquier otro status que sí respondió el backend (5xx, o algo
        // inesperado) es un error "del servidor", no de la conexión.
        setServerError(true);
      } else {
        // Sin .status en absoluto: el fetch falló de plano (sin conexión,
        // timeout, DNS) — ver getCatalogoPublico en apiService.js.
        setNetworkError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchCatalogo();
  }, [fetchCatalogo]);

  // Categorías presentes en el catálogo ya cargado (todo del lado del
  // cliente: el endpoint público no pagina — ver nota en el README del
  // backend). Los productos sin categoría se agrupan en "Otros" en vez de
  // quedar fuera del filtro.
  const categorias = useMemo(() => {
    if (!catalogo) return [];
    const set = new Set();
    let hayOtros = false;
    catalogo.products.forEach((p) => {
      const cat = (p.categoria || '').trim();
      if (cat) set.add(cat);
      else hayOtros = true;
    });
    const lista = Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
    if (hayOtros) lista.push(OTROS_LABEL);
    return lista;
  }, [catalogo]);

  // Buscador (por nombre, tolerante a mayúsculas/tildes/plurales) y filtro
  // de categoría combinados — puramente visual, no toca el carrito.
  const productosFiltrados = useMemo(() => {
    if (!catalogo) return [];
    return catalogo.products.filter((p) => {
      const cat = (p.categoria || '').trim() || OTROS_LABEL;
      const categoriaMatch = !selectedCategoria || cat === selectedCategoria;
      const searchMatch = !search.trim() || matchesSearch(p.nombre, search);
      return categoriaMatch && searchMatch;
    });
  }, [catalogo, search, selectedCategoria]);

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategoria(null);
  };

  const addToCart = (product) => {
    setCart((prev) => {
      const existente = prev.find((item) => item.productId === product.id);
      if (existente) {
        return prev.map((item) =>
          item.productId === product.id ? { ...item, cantidad: item.cantidad + 1 } : item
        );
      }
      return [...prev, { productId: product.id, nombre: product.nombre, precioVenta: Number(product.precioVenta), cantidad: 1, imagen: product.imagenes[0] }];
    });
  };

  const setCantidad = (productId, cantidad) => {
    setCart((prev) => prev.map((item) => (item.productId === productId ? { ...item, cantidad } : item)));
  };

  // Cantidad elegida directamente en la tarjeta: 0 lo saca del carrito, más
  // de 0 lo agrega (si no estaba) con esa cantidad exacta.
  const setCantidadProducto = (product, cantidad) => {
    if (cantidad <= 0) return removeFromCart(product.id);
    setCart((prev) =>
      prev.some((item) => item.productId === product.id)
        ? prev.map((item) => (item.productId === product.id ? { ...item, cantidad } : item))
        : [...prev, { productId: product.id, nombre: product.nombre, precioVenta: Number(product.precioVenta), cantidad, imagen: product.imagenes[0] }]
    );
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  };

  const closeDetalle = useCallback(() => setDetalleId(null), []);

  const cartTotal = cart.reduce((sum, item) => sum + item.precioVenta * item.cantidad, 0);
  const cartCantidad = cart.reduce((sum, item) => sum + item.cantidad, 0);

  const handleEnviarPedido = async (e) => {
    e.preventDefault();
    setError(null);

    if (!nombre.trim() || !telefono.trim()) {
      setError('Tu nombre y celular son obligatorios.');
      return;
    }
    if (tipoEntrega === 'DOMICILIO' && !direccionEntrega.trim()) {
      setError('La dirección es obligatoria para domicilio.');
      return;
    }
    if (!autorizaDatos) {
      setError(AUTORIZACION_FALTANTE_MSG);
      return;
    }

    setEnviando(true);
    try {
      const resultado = await crearPedidoPublico(slug, {
        items: cart.map((item) => ({ productId: item.productId, cantidad: item.cantidad })),
        cliente: { nombre: nombre.trim(), telefono: telefono.trim() },
        tipoEntrega,
        direccionEntrega: tipoEntrega === 'DOMICILIO' ? direccionEntrega.trim() : undefined,
        autorizacionDatos: true,
        aceptaPromociones,
      }, codigoVendedor);

      if (resultado.whatsappUrl) {
        window.location.href = resultado.whatsappUrl;
      }
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
    } catch (err) {
      setError(err.message || 'No se pudo enviar el pedido.');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (networkError || serverError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4 px-4 text-center">
        <FaExclamationTriangle size={48} className="text-amber-400" />
        <h1 className="text-xl font-black text-gray-700">
          {networkError ? 'No pudimos cargar el catálogo' : 'Algo falló de nuestro lado'}
        </h1>
        <p className="text-gray-400 text-sm max-w-xs">
          {networkError
            ? 'Revisa tu conexión e intenta de nuevo.'
            : 'Fue un error temporal. Intenta de nuevo en un momento.'}
        </p>
        <button
          onClick={fetchCatalogo}
          className="mt-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-black rounded-xl transition-all active:scale-95"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (notFound || !catalogo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4 px-4 text-center">
        <FaBoxOpen size={48} className="text-gray-300" />
        <h1 className="text-xl font-black text-gray-700">Catálogo no encontrado</h1>
        <p className="text-gray-400 text-sm">Este link no corresponde a ningún catálogo activo.</p>
      </div>
    );
  }

  const { company, products } = catalogo;
  // Responsable de los datos de los compradores: la tienda. El backend manda
  // su contacto en responsableDatos; el respaldo cubre un backend anterior.
  const tienda = company.responsableDatos || { nombre: company.nombre, telefono: company.whatsappVentas?.[0] || null };
  const productoDetalle = detalleId !== null ? products.find((p) => p.id === detalleId) : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20">
      {/* Portada */}
      <header className="bg-gray-900 text-white">
        {company.fotoPortadaCatalogo && (
          <div className="h-40 md:h-56 w-full overflow-hidden">
            <img src={company.fotoPortadaCatalogo} alt="" className="w-full h-full object-cover opacity-70" />
          </div>
        )}
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center flex-shrink-0">
            <FaStore size={20} />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black">{company.nombre}</h1>
            {company.descripcionCatalogo && (
              <p className="text-gray-300 text-sm max-w-xl">{company.descripcionCatalogo}</p>
            )}
          </div>
        </div>
      </header>

      {/* Buscador + filtro de categoría: pegado arriba (sticky) para que no
          haya que volver a subir a buscar después de hacer scroll, y el
          scroll horizontal de los chips es propio (no mueve la página). */}
      {products.length > 0 && (
        <div className={`sticky ${STICKY_TOP} z-30 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100 px-4 py-3`}>
          <div className="max-w-5xl mx-auto w-full space-y-2">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={12} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto..."
                className="w-full pl-9 pr-9 py-2.5 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                  aria-label="Limpiar búsqueda"
                >
                  <FaTimesCircle size={14} />
                </button>
              )}
            </div>

            {categorias.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
                <button
                  onClick={() => setSelectedCategoria(null)}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-black whitespace-nowrap transition-all ${
                    !selectedCategoria ? 'bg-purple-600 text-white' : 'bg-white text-gray-500 border border-gray-200'
                  }`}
                >
                  Todas
                </button>
                {categorias.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategoria(cat)}
                    className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-black whitespace-nowrap transition-all ${
                      selectedCategoria === cat ? 'bg-purple-600 text-white' : 'bg-white text-gray-500 border border-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Productos */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">
        {products.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <FaBoxOpen size={40} className="mx-auto mb-3" />
            <p className="font-bold">Todavía no hay productos publicados.</p>
          </div>
        ) : productosFiltrados.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <FaSearch size={40} className="mx-auto mb-3 opacity-40" />
            <p className="font-bold">Ningún producto coincide con tu búsqueda.</p>
            <button onClick={handleClearFilters} className="mt-3 text-xs font-black text-purple-600 hover:underline">
              Limpiar filtros
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {productosFiltrados.map((p) => {
              const enCarrito = cart.find((item) => item.productId === p.id);
              return (
                <div key={p.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                  <button
                    type="button"
                    onClick={() => setDetalleId(p.id)}
                    aria-label={`Ver detalle de ${p.nombre}`}
                    className="aspect-square bg-gray-100 relative block w-full text-left"
                  >
                    {p.imagenes[0] ? (
                      <img src={p.imagenes[0]} alt={p.nombre} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <FaBoxOpen size={32} />
                      </div>
                    )}
                    {!p.disponible && (
                      <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-black px-2 py-1 rounded-full uppercase">
                        Agotado
                      </span>
                    )}
                    <span className="absolute bottom-2 right-2 p-1.5 bg-white/85 rounded-full text-gray-500 pointer-events-none">
                      <FaExpand size={10} />
                    </span>
                  </button>
                  <div className="p-3 flex-1 flex flex-col">
                    <button type="button" onClick={() => setDetalleId(p.id)} className="text-left flex-1">
                      <span className="block font-bold text-gray-800 text-sm leading-tight">{p.nombre}</span>
                    </button>
                    {p.categoria && <p className="text-[10px] text-gray-400 uppercase font-bold mt-1">{p.categoria}</p>}
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="font-black text-purple-600">{formatCOP(p.precioVenta)}</p>
                      {!p.esDigital && p.disponible && (
                        <QuantityInput
                          value={enCarrito ? enCarrito.cantidad : 0}
                          onChange={(n) => setCantidadProducto(p, n)}
                          onRemove={() => removeFromCart(p.id)}
                          variant="compact"
                        />
                      )}
                    </div>

                    {p.esDigital && (
                      <button
                        onClick={() => setCompraDigital(p)}
                        className="mt-2 w-full py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-black rounded-xl transition-all active:scale-95"
                      >
                        Comprar ahora
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Botón flotante del carrito */}
      {cartCantidad > 0 && !showCart && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6 py-3.5 shadow-2xl flex items-center gap-3 font-black text-sm z-40 active:scale-95 transition-all"
        >
          <FaShoppingCart /> {cartCantidad} · {formatCOP(cartTotal)}
        </button>
      )}

      {/* Panel del carrito / checkout */}
      {showCart && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h3 className="font-black text-gray-800 flex items-center gap-2"><FaShoppingCart className="text-purple-600" /> Tu pedido</h3>
              <button onClick={() => { setShowCart(false); setShowCheckout(false); }} className="p-2 rounded-xl text-gray-400 hover:bg-gray-100">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {cart.map((item) => (
                <div key={item.productId} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                  {item.imagen ? (
                    <img src={item.imagen} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-800 truncate">{item.nombre}</p>
                    <p className="text-xs text-gray-400">{formatCOP(item.precioVenta)} c/u</p>
                  </div>
                  <QuantityInput
                    value={item.cantidad}
                    onChange={(n) => setCantidad(item.productId, n)}
                    onRemove={() => removeFromCart(item.productId)}
                    variant="cart"
                  />
                </div>
              ))}

              {showCheckout && (
                <form onSubmit={handleEnviarPedido} className="pt-4 border-t border-gray-100 space-y-3">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase">Tu nombre</label>
                    <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} required
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase">Tu celular</label>
                    <input type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} required
                      placeholder="3001234567"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Entrega</label>
                    <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl">
                      <button type="button" onClick={() => setTipoEntrega('RECOGE')}
                        className={`py-2 rounded-lg text-xs font-black transition-all ${tipoEntrega === 'RECOGE' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-400'}`}>
                        Recoger
                      </button>
                      <button type="button" onClick={() => setTipoEntrega('DOMICILIO')} disabled={!company.ofreceDomicilio}
                        className={`py-2 rounded-lg text-xs font-black transition-all disabled:opacity-30 ${tipoEntrega === 'DOMICILIO' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-400'}`}>
                        Domicilio
                      </button>
                    </div>
                    {!company.ofreceDomicilio && <p className="text-[10px] text-gray-400 mt-1">Esta tienda no ofrece domicilio.</p>}
                  </div>

                  {tipoEntrega === 'DOMICILIO' && (
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase">Dirección de entrega</label>
                      <input type="text" value={direccionEntrega} onChange={(e) => setDireccionEntrega(e.target.value)} required
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                      {company.valorDomicilioDefault != null && (
                        <p className="text-[10px] text-gray-400 mt-1">Domicilio: {formatCOP(company.valorDomicilioDefault)}</p>
                      )}
                    </div>
                  )}

                  <AutorizacionDatos
                    tienda={tienda}
                    autoriza={autorizaDatos}
                    onAutoriza={setAutorizaDatos}
                    promociones={aceptaPromociones}
                    onPromociones={setAceptaPromociones}
                    idPrefix="pedido"
                  />

                  {error && <p className="text-red-500 text-xs font-bold">{error}</p>}

                  <button type="submit" disabled={enviando}
                    className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-all">
                    <FaWhatsapp size={18} /> {enviando ? 'Enviando...' : 'Enviar pedido por WhatsApp'}
                  </button>
                </form>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black text-gray-400 uppercase">Total</span>
                <span className="text-xl font-black text-purple-600">{formatCOP(cartTotal)}</span>
              </div>
              {!showCheckout && (
                <button
                  onClick={() => setShowCheckout(true)}
                  disabled={cart.length === 0}
                  className="w-full py-3.5 bg-purple-600 hover:bg-purple-700 text-white font-black rounded-xl disabled:opacity-40 active:scale-95 transition-all"
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detalle del producto (foto ampliada, galería, descripción) */}
      {productoDetalle && (
        <ProductDetailModal
          product={productoDetalle}
          cantidad={cart.find((item) => item.productId === productoDetalle.id)?.cantidad || 0}
          onClose={closeDetalle}
          onAdd={() => addToCart(productoDetalle)}
          onChangeCantidad={(n) => setCantidad(productoDetalle.id, n)}
          onRemove={() => removeFromCart(productoDetalle.id)}
          onComprarDigital={() => { setDetalleId(null); setCompraDigital(productoDetalle); }}
        />
      )}

      {/* Compra de un producto digital: pide los datos y lleva al link de pago */}
      {compraDigital && (
        <DigitalPurchaseModal
          slug={slug}
          codigoVendedor={codigoVendedor}
          product={compraDigital}
          tienda={tienda}
          onClose={() => setCompraDigital(null)}
        />
      )}

      {/* Pie "Hecho con Vendita" */}
      <footer className="py-6 text-center border-t border-gray-100 bg-white mt-auto space-y-2">
        <button
          type="button"
          onClick={() => setVerPolitica(true)}
          className="block mx-auto text-xs font-bold text-gray-400 hover:text-purple-600 underline transition-colors"
        >
          Política de tratamiento de datos
        </button>
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400 hover:text-purple-600 transition-colors">
          Hecho con <span className="text-purple-600">Vendita</span> <FaExternalLinkAlt size={9} />
        </Link>
      </footer>

      {verPolitica && <PoliticaDatosTienda tienda={tienda} onClose={() => setVerPolitica(false)} />}
    </div>
  );
};

export default PublicCatalogPage;
